/**
 * Fetches XML content from given path
 */
const getFileContents = (path: string) => {
  const request = new XMLHttpRequest();
  request.open('GET', path, false);
  request.send();

  return request.responseXML;
};

/**
 * Get content from a DocumentFragment and return HTML as text
 */
const getStringFromDocumentFragment = (fragment: DocumentFragment) => {
  const element = document.createElement('div');
  element.appendChild(fragment);

  return element.innerHTML;
};

/**
 * ContentParser
 *
 * A class to get your XML content parsed to HTML.
 */
export class ContentParser {
  readonly #parser = new XSLTProcessor();

  readonly #domParser = new DOMParser();

  constructor(stylesheetUrl: string) {
    this.#setStylesheets(stylesheetUrl);
  }

  /**
   * Get XML content from path and transform to HTML
   * @param path path to the XML file you want to transform
   * @returns HTML as string
   */
  public parseFromPath(path: string, includeSourceMaterial = true) {
    const response = getFileContents(path);
    if (!response) return undefined;

    if (includeSourceMaterial) {
      this.#parser.setParameter(
        null,
        'includeSourceMaterial',
        includeSourceMaterial,
      );
    }

    return this.#transform(response);
  }

  public parseFromString(content: string, includeSourceMaterial = true) {
    if (includeSourceMaterial) {
      this.#parser.setParameter(
        null,
        'includeSourceMaterial',
        includeSourceMaterial,
      );
    }

    const xml = this.#domParser.parseFromString(content, 'text/xml');
    return this.#transform(xml);
  }

  #transform(content: Document) {
    const html = this.#parser.transformToFragment(content, document);
    return getStringFromDocumentFragment(html);
  }

  #setStylesheets(path: string): void {
    const content = getFileContents(path);
    if (!content) return;

    this.#parser.importStylesheet(content);
  }
}

export const parseContent = (path: string) => {
  const parser = new ContentParser('/public/index.xsl');
  return parser.parseFromPath(path);
};
